import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const BADGE = { PENDING:"bg-[#FDE047]", APPROVED:"bg-[#86EFAC]", REJECTED:"bg-[#FCA5A5]" };

// mode: "create" (accountant) | "review" (admin/founder)
export default function LoansTransfers({ mode = "review" }) {
  const [divs, setDivs] = useState([]);
  const [rows, setRows] = useState([]);
  const [kind, setKind] = useState("EXTERNAL_LOAN");
  const [f, setF] = useState({});
  const [reject, setReject] = useState(null);
  const [reason, setReason] = useState("");
  const load = () => api.get("/finance/loans").then(r=>setRows(r.data)).catch(()=>{});
  useEffect(()=>{ load(); api.get("/finance-settings/divisions").then(r=>setDivs(r.data)).catch(()=>{}); }, []);

  const emiPreview = () => {
    const P=+f.amount||0, N=+f.tenure_n||0, R=+f.interest_rate||0;
    if(!P||!N) return 0;
    if(kind==="INTERNAL_TRANSFER"||!R) return (P/N).toFixed(2);
    const r=R/1200, x=(1+r)**N; return (P*r*x/(x-1)).toFixed(2);
  };
  const create = async () => {
    try {
      await api.post("/finance/loans", {kind, ...f, amount:+f.amount||0, tenure_n:+f.tenure_n||0,
        interest_rate:+f.interest_rate||0});
      toast.success("Submitted for approval"); setF({}); load();
    } catch(e){ toast.error(e?.response?.data?.detail||"Failed"); }
  };
  const approve = async (id) => { try{ const r=await api.post(`/finance/loans/${id}/approve`);
    toast.success(r.data.idempotent?"Already approved":"Approved & posted to ledger"); load(); }catch(e){toast.error(e?.response?.data?.detail||"Failed");} };
  const doReject = async () => { if(!reason.trim()) return toast.error("Reason required");
    try{ await api.post(`/finance/loans/${reject}/reject`,{reason}); toast.success("Rejected"); setReject(null); setReason(""); load(); }catch(e){toast.error(e?.response?.data?.detail||"Failed");} };
  const runEmis = async () => { try{ const r=await api.post("/finance/loans/run-emi-cycle",{});
    toast.success(`EMI cycle ${r.data.period}: ${r.data.posted} posted, ${r.data.skipped} skipped`); load(); }catch(e){toast.error(e?.response?.data?.detail||"Failed");} };

  return (
    <div className="space-y-5">
      {mode==="create" && (
        <div className="nb-card p-6 bg-[#93C5FD] space-y-3">
          <h3 className="display text-xl font-bold">New Loan / Transfer</h3>
          <div className="flex gap-2">
            {["EXTERNAL_LOAN","INTERNAL_TRANSFER"].map(k=>(
              <button key={k} onClick={()=>{setKind(k);setF({});}}
                className={`nb-btn text-xs ${kind===k?"":"nb-btn-ghost"}`} data-testid={`lt-kind-${k}`}>
                {k==="EXTERNAL_LOAN"?"External Loan":"Internal Transfer"}</button>
            ))}
          </div>
          {kind==="EXTERNAL_LOAN" ? (
            <div className="grid md:grid-cols-2 gap-3">
              <input className="nb-input" placeholder="Lender Name" value={f.lender_name||""} onChange={e=>setF({...f,lender_name:e.target.value})} data-testid="lt-lender"/>
              <input className="nb-input mono" placeholder="Mobile (optional)" value={f.mobile_number||""} onChange={e=>setF({...f,mobile_number:e.target.value.replace(/\D/g,"").slice(0,10)})} data-testid="lt-mobile"/>
              <input type="number" className="nb-input mono" placeholder="Loan Amount (P)" value={f.amount||""} onChange={e=>setF({...f,amount:e.target.value})} data-testid="lt-amount"/>
              <input type="number" className="nb-input mono" placeholder="Annual Interest % (R)" value={f.interest_rate||""} onChange={e=>setF({...f,interest_rate:e.target.value})} data-testid="lt-rate"/>
              <input type="number" className="nb-input mono" placeholder="Tenure months (N)" value={f.tenure_n||""} onChange={e=>setF({...f,tenure_n:e.target.value})} data-testid="lt-tenure"/>
              <select className="nb-select" value={f.target_division_id||""} onChange={e=>setF({...f,target_division_id:e.target.value})} data-testid="lt-target">
                <option value="">Target Branch…</option>{divs.map(d=><option key={d.division_id} value={d.division_id}>{d.name}</option>)}</select>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              <input type="number" className="nb-input mono" placeholder="Transfer Amount (P)" value={f.amount||""} onChange={e=>setF({...f,amount:e.target.value})} data-testid="lt-amount"/>
              <input type="number" className="nb-input mono" placeholder="Number of EMIs (N)" value={f.tenure_n||""} onChange={e=>setF({...f,tenure_n:e.target.value})} data-testid="lt-tenure"/>
              <select className="nb-select" value={f.from_division_id||""} onChange={e=>setF({...f,from_division_id:e.target.value})} data-testid="lt-from">
                <option value="">From Branch (lender)…</option>{divs.map(d=><option key={d.division_id} value={d.division_id}>{d.name}</option>)}</select>
              <select className="nb-select" value={f.to_division_id||""} onChange={e=>setF({...f,to_division_id:e.target.value})} data-testid="lt-to">
                <option value="">To Branch (borrower)…</option>{divs.filter(d=>d.division_id!==f.from_division_id).map(d=><option key={d.division_id} value={d.division_id}>{d.name}</option>)}</select>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="mono text-sm">Computed EMI: <b>₹{emiPreview()}</b> · due 15th monthly</span>
            <button className="nb-btn" onClick={create} data-testid="lt-submit">Submit for Approval</button>
          </div>
        </div>
      )}

      <div className="nb-card p-0 overflow-hidden">
        <div className="px-6 py-3 border-b-2 border-black bg-white flex items-center justify-between">
          <h3 className="display text-lg font-bold">Loans &amp; Transfers</h3>
          {mode==="review" && <button className="nb-btn nb-btn-secondary text-xs" onClick={runEmis} data-testid="lt-run-emi">Process Monthly EMIs</button>}
        </div>
        <table className="nb-table">
          <thead><tr><th>Type</th><th>Party / Branches</th><th>Amount</th><th>EMI</th><th>Paid</th><th>Next EMI</th><th>Status</th>{mode==="review"&&<th>Actions</th>}</tr></thead>
          <tbody>
            {rows.map(r=>(
              <tr key={r.loan_id} data-testid={`lt-row-${r.loan_id}`}>
                <td className="text-xs font-bold">{r.kind==="EXTERNAL_LOAN"?"Ext. Loan":"Int. Transfer"}</td>
                <td className="text-xs">{r.kind==="EXTERNAL_LOAN"?`${r.lender_name} → ${r.target_division_name}`:`${r.from_division_name} → ${r.to_division_name}`}</td>
                <td className="mono">₹{(r.amount||0).toLocaleString()}</td>
                <td className="mono">₹{(r.emi_amount||0).toLocaleString()}</td>
                <td className="mono text-xs">{r.emis_paid||0}/{r.tenure_n}</td>
                <td className="mono text-xs">{(r.due_dates||[])[r.emis_paid||0]||"—"}</td>
                <td><span className={`nb-badge ${BADGE[r.status]}`}>{r.status}</span></td>
                {mode==="review" && <td>
                  {r.status==="PENDING" ? <div className="flex gap-1">
                    <button className="nb-btn text-xs" onClick={()=>approve(r.loan_id)} data-testid={`lt-approve-${r.loan_id}`}>Approve</button>
                    <button className="nb-btn nb-btn-danger text-xs" onClick={()=>setReject(r.loan_id)} data-testid={`lt-reject-${r.loan_id}`}>Reject</button>
                  </div> : (r.reject_reason ? <span className="text-xs text-rose-600" title={r.reject_reason}>Rejected</span> : <span className="text-xs text-slate-400">—</span>)}
                </td>}
              </tr>
            ))}
            {rows.length===0 && <tr><td colSpan={mode==="review"?8:7} className="text-center py-6 text-slate-500">No records yet</td></tr>}
          </tbody>
        </table>
      </div>

      {reject && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={()=>{setReject(null);setReason("");}}>
          <div className="nb-card bg-white p-5 max-w-md w-full space-y-3" onClick={e=>e.stopPropagation()}>
            <h4 className="display font-bold">Reject — reason required</h4>
            <textarea className="nb-input" rows="3" value={reason} onChange={e=>setReason(e.target.value)} data-testid="lt-reject-reason"/>
            <div className="flex gap-2 justify-end">
              <button className="nb-btn nb-btn-ghost text-xs" onClick={()=>setReject(null)}>Cancel</button>
              <button className="nb-btn nb-btn-danger text-xs" onClick={doReject} data-testid="lt-reject-confirm">Confirm Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
